import type { VoyagerInput } from '@core/reducers/inputsSlice';
import type { LoadContactReturn } from '@core/services/hub';
import fromLdsStartDate from '@core/utils/fromLdsStartDate';

// converts data from the contactdb into voyager inputs.
// please note that this logic was largely reverse engineered from how voyager stores its data, so
// there is a large margin for error here.  in addition, not all values in contactdb are immediately tranferrable
// to the voyager inputs, so the conversion process is lossy
export function hubLoadContactReturnAsVoyagerInputs(input: LoadContactReturn): VoyagerInput[] {
  const output: VoyagerInput[] = [];

  // "zip": "28173",
  if (input.transaction.details.location?.postalCode) {
    output.push({
      key: 'zip',
      value: input.transaction.details.location?.postalCode,
    });
  }
  // "ipInferredPostalCode": "28173",
  if (input.transaction.details.location?.ipInferredPostalCode) {
    output.push({
      key: 'ipInferredPostalCode',
      value: input.transaction.details.location?.ipInferredPostalCode,
    });
  }
  // "educationLevel": "bachelors",
  if (input.transaction.details.levelOfEducation) {
    output.push({
      key: 'educationLevel',
      value: input.transaction.details.levelOfEducation,
    });
  }
  // currentLicense: "SOMETHING"
  if (input.transaction.details.currentLicense) {
    output.push({
      key: 'currentLicense',
      value: input.transaction.details.currentLicense,
    });
  }
  // "graduationYear": "1998",
  if (input.transaction.details.hsGradYear) {
    output.push({
      key: 'graduationYear',
      value: `${input.transaction.details.hsGradYear}`,
    });
  }
  // UNSUPPORTED: "decision-factors": [{ "label": "Affordability" } ],
  // UNSUPPORTED: "reason-education": { "label": "Switch careers" },
  // "startTime": 12,
  if (input.transaction.details.startDate) {
    // stored as a real date instead of a number :sad:
    output.push({
      key: 'startTime',
      value: fromLdsStartDate(input.transaction.details.startDate),
    });
  }
  // "militaryAffiliation": false,
  if (typeof input.transaction.details.militaryStatus !== 'undefined') {
    const val = !!input.transaction.details.militaryStatus; // force to be bool
    output.push({
      key: 'militaryAffiliation',
      value: val,
      options: {
        label: val ? 'Yes' : 'No',
        value: val,
        icon: val ? 'yes' : 'no',
      },
    });
  }
  // "currentPermanentResidenceCountry": "us",
  if (input.transaction.details.currentPermanentResidenceCountry) {
    output.push({
      key: 'country', // translates to country in voyager
      value: input.transaction.details.currentPermanentResidenceCountry === 'us' ? 'us' : 'other',
    });
  }
  // "firstName": "test",
  if (input.transaction.details.firstName) {
    output.push({
      key: 'firstName',
      value: input.transaction.details.firstName,
    });
  }
  // "lastName": "mctest",
  if (input.transaction.details.lastName) {
    output.push({
      key: 'lastName',
      value: input.transaction.details.lastName,
    });
  }
  // "emailConsent": false,
  // "phone": "(704) 993-9929",
  if (input.transaction.details.phoneNumber) {
    output.push({
      key: 'phone',
      value: input.transaction.details.phoneNumber,
    });
  }
  // "email": "test.mctest@highereducation.com"
  if (input.transaction.details.email) {
    output.push({
      key: 'email',
      value: input.transaction.details.email,
    });
  }

  // hasGivenTCPAConsent
  if (typeof input.transaction.details.hasGivenTCPAConsent !== 'undefined') {
    const val = !!input.transaction.details.hasGivenTCPAConsent; // force to be bool
    output.push({
      key: 'hasGivenTCPAConsent',
      value: val,
    });
  }
  // hasGivenProspectSharingConsent
  if (typeof input.transaction.details.hasGivenProspectSharingConsent !== 'undefined') {
    const val = !!input.transaction.details.hasGivenProspectSharingConsent; // force to be bool
    output.push({
      key: 'hasGivenProspectSharingConsent',
      value: val,
    });
  }
  // hasGivenThirdPartySharingConsent
  if (typeof input.transaction.details.hasGivenThirdPartySharingConsent !== 'undefined') {
    const val = !!input.transaction.details.hasGivenThirdPartySharingConsent; // force to be bool
    output.push({
      key: 'hasGivenThirdPartySharingConsent',
      value: val,
    });
  }

  // thirdPartyLeadId: "1234567890"
  if (input.transaction.details.thirdPartyLeadId) {
    output.push({
      key: 'activeProspectToken',
      value: input.transaction.details.thirdPartyLeadId,
    });
  }

  // gpa: 3.0
  if (input.transaction.details.gpa) {
    output.push({
      key: 'gpa',
      value: input.transaction.details.gpa,
    });
  }

  // yearsOfWorkExperience: 5
  if (input.transaction.details.yearsOfWorkExperience) {
    output.push({
      key: 'yearsOfWorkExperience',
      value: input.transaction.details.yearsOfWorkExperience,
    });
  }

  // yearsOfProgramRelatedExperience: 2
  if (input.transaction.details.yearsOfProgramRelatedExperience) {
    output.push({
      key: 'yearsOfProgramRelatedExperience',
      value: input.transaction.details.yearsOfProgramRelatedExperience,
    });
  }

  // currentAcademicAward: "bsn"
  if (input.transaction.details.currentAcademicAward) {
    output.push({
      key: 'currentAcademicAward',
      value: input.transaction.details.currentAcademicAward,
    });
  }

  // inProgressAcademicAward: "bsn"
  if (input.transaction.details.inProgressAcademicAward) {
    output.push({
      key: 'inProgressAcademicAward',
      value: input.transaction.details.inProgressAcademicAward,
    });
  }

  // transferCredits: 12
  if (input.transaction.details.transferCredits) {
    output.push({
      key: 'transferCredits',
      value: input.transaction.details.transferCredits,
    });
  }

  // workExperienceField
  if (input.transaction.details.workExperienceField) {
    output.push({
      key: 'workExperienceField',
      value: input.transaction.details.workExperienceField,
    });
  }

  // undergraduateMajor
  if (input.transaction.details.undergraduateMajor) {
    output.push({
      key: 'undergraduateMajor',
      value: input.transaction.details.undergraduateMajor,
    });
  }

  // someCollege
  if (input.transaction.details.someCollege) {
    output.push({
      key: 'someCollege',
      value: input.transaction.details.someCollege,
    });
  }

  return output;
}
